// supervisorBridge.js
//
// Pure-JS helpers that translate between the Builder canvas (nodes +
// connections) and the v13 Supervisor data shape persisted on the
// `agents` row (`agent_kind` + `flow_config.supervisor.{mode,experts,...}`).
//
// Design intent
// -------------
// The v13 Supervisor architecture (Notes/agentic-supervisor-architecture-v1.md)
// converged on FOUR execution modes — "fan_out", "sequential",
// "conditional", "review_loop" — pinned in
//   backend/app/services/supervisor/types.py::VALID_SUPERVISOR_MODES.
//
// The Builder canvas already exposes four orchestration templates
// (sequential-chain / fan-out-fan-in / conditional-routing / supervisor)
// which map 1:1 onto the supervisor modes.  This module is the
// translation layer the BuilderView calls at save-time and load-time so
// canvas authors don't have to know v13 internals.
//
// Phase-1 scope (per project tracker, Phase 2.4 "Builder bridge"):
//   * Detect a v13 supervisor pattern from canvas state (inferShape).
//   * Build the patch the BuilderView merges into its agent payload at
//     save time (buildSupervisorPatch).
//   * Pick the right canvas template to hydrate when an agent_kind=
//     'supervisor' row is loaded from the API (pickTemplateForLoadedSupervisor).
//
// Out of scope (deliberate phase-2 polish):
//   * Binding specialist nodes' agentId from a use-case install record
//     (the `experts` field is an array of *names*, not agent IDs).
//   * Auto-clearing channels on the bound specialist agents — that's
//     the specialist's own save handler's job; we only WARN here.
//   * Editing `flow_config.supervisor.exemplars` from the canvas.

import { ORCHESTRATION_TEMPLATES } from "./constants";
import { clampRoutingConfig } from "../library/viewer/routingSensitivity";


// ---------------------------------------------------------------------
// Constant: canvas-template-id  →  v13 supervisor mode mapping.
//
// The "supervisor" entry maps to "fan_out" by default; if the canvas
// also carries any connection of `type: "loop"` (e.g. the "Report back"
// edges from specialists back to the supervisor in the default template),
// we promote the mode to "review_loop".
//
// This constant is the SOURCE OF TRUTH the bridge consults in both
// directions (save: template → mode, load: mode → template).  Keep it
// in sync with VALID_SUPERVISOR_MODES on the backend if a new mode
// lands.
// ---------------------------------------------------------------------
export const MODE_BY_TEMPLATE_ID = Object.freeze({
  "supervisor": "fan_out",          // promote to "review_loop" if loop edges
  "fan-out-fan-in": "fan_out",
  "sequential-chain": "sequential",
  "conditional-routing": "conditional",
});


// ---------------------------------------------------------------------
// Helper: collect agent-typed nodes (specialists / experts / workers) on
// the canvas.  Excludes the "supervisor" agent node itself when we can
// identify it; the supervisor is the agent we're SAVING, not an expert.
//
// In the canvas templates the supervisor node is agent-typed too, but
// it's labelled "Supervisor" and its `executionMode` is "supervisor".
// Specialists carry a different executionMode ("conditional",
// "sequential", "parallel").  We use that signal to filter.
// ---------------------------------------------------------------------
function _collectExpertNodes(nodes) {
  if (!Array.isArray(nodes)) return [];
  return nodes.filter((n) => {
    if (!n || n.type !== "agent") return false;
    const execMode = n.agentConfig?.executionMode;
    // Supervisor's own node uses executionMode="supervisor"; everything
    // else (sequential / parallel / conditional) is treated as an expert.
    return execMode !== "supervisor";
  });
}


// ---------------------------------------------------------------------
// Helper: detect "the canvas has at least one loop edge" — i.e. any
// connection whose `type === "loop"`.  In the supervisor canvas template
// the "Report back" edges from each specialist back to the supervisor
// node carry `type: "loop"`; that's the convention we key on to promote
// fan_out → review_loop.
//
// We're permissive about the connection shape (just look at the `type`
// field) so users who hand-edit connections still get the right mode
// inferred without us policing source/target node types.
// ---------------------------------------------------------------------
function _hasLoopEdge(connections) {
  if (!Array.isArray(connections)) return false;
  return connections.some((c) => c && c.type === "loop");
}


// ---------------------------------------------------------------------
// Helper: heuristic detection of WHICH canvas template the user picked
// (without needing to track the click — we infer from connection types
// + node executionModes).  Returns one of the keys of MODE_BY_TEMPLATE_ID,
// or null if the canvas does not look like any supervisor pattern.
//
// Heuristic priority:
//   1. If any agent node has executionMode === "supervisor"  →  "supervisor".
//   2. Else if any connection.type === "conditional"         →  "conditional-routing".
//   3. Else if any connection.type === "parallel"            →  "fan-out-fan-in".
//   4. Else if there are >=2 agent nodes connected in series →  "sequential-chain".
//   5. Else                                                  →  null (not a v13 pattern).
//
// Cases 2-4 detect canvases authored from the orchestration templates
// even when the supervisor-typed node is absent.  Case 1 is the
// conventional "Supervisor Pattern" template the user dragged on.
// ---------------------------------------------------------------------
function _detectTemplateId(nodes, connections) {
  if (!Array.isArray(nodes) || nodes.length === 0) return null;

  const agentNodes = nodes.filter((n) => n && n.type === "agent");
  if (agentNodes.length === 0) return null;

  // Case 1 — explicit supervisor mode.
  const hasSupervisorMode = agentNodes.some(
    (n) => n.agentConfig?.executionMode === "supervisor",
  );
  if (hasSupervisorMode) return "supervisor";

  // Case 2-3 — connection-type signals.
  if (Array.isArray(connections)) {
    if (connections.some((c) => c && c.type === "conditional")) {
      return "conditional-routing";
    }
    if (connections.some((c) => c && c.type === "parallel")) {
      return "fan-out-fan-in";
    }
  }

  // Case 4 — multi-expert canvas (the robust catch-all).
  //
  // BUGFIX (Supervisor D, 2026-09-10): the most natural authoring path —
  // delete the template's default Agent A/B, drag fresh Sub-Agents from the
  // palette, and wire them through the "Router" (condition) node — produces
  // NONE of the signals above: palette-dragged Sub-Agents default to
  // executionMode="sequential" (not "supervisor"/"conditional"/"parallel"),
  // user-drawn edges carry NO `type` (only the template's own edges do), and
  // the experts fan out from the condition node rather than agent→agent. So a
  // clearly-multi-agent supervisor was saved as a plain regular agent and every
  // expert was silently dropped.
  //
  // Fix: ≥2 CONNECTED agent (expert) nodes IS a supervisor pattern regardless
  // of how they're wired. Detect on that, then infer the mode from the best
  // available signal (a Router/condition node → conditional; else the experts'
  // own executionMode; else sequential). Requiring the experts to touch at
  // least one edge avoids flipping on stray/orphan agent nodes.
  const expertNodes = agentNodes.filter(
    (n) => n.agentConfig?.executionMode !== "supervisor",
  );
  const conns = Array.isArray(connections) ? connections : [];
  const connectedExperts = expertNodes.filter((e) =>
    conns.some((c) => c && (c.from === e.id || c.to === e.id)),
  );
  if (connectedExperts.length >= 2) {
    // A Router/condition node fanning to the experts → conditional routing.
    if (nodes.some((n) => n && n.type === "condition")) return "conditional-routing";
    // Otherwise take the strongest per-expert executionMode signal.
    if (expertNodes.some((n) => n.agentConfig?.executionMode === "conditional")) return "conditional-routing";
    if (expertNodes.some((n) => n.agentConfig?.executionMode === "parallel")) return "fan-out-fan-in";
    return "sequential-chain";
  }

  return null;
}


// ---------------------------------------------------------------------
// PUBLIC: inferSupervisorShape(nodes, connections)
//
// Returns null when the canvas does NOT express a v13 supervisor
// pattern; otherwise returns:
//
//   {
//     mode: <one of VALID_SUPERVISOR_MODES>,
//     experts: [{name, agentId}, ...],
//     max_iterations?: number,
//     templateId: <one of MODE_BY_TEMPLATE_ID keys>,  // for hydration
//   }
//
// Caller (BuilderView save handler) uses this to decide whether to
// flip `agent_kind` to "supervisor" and write `flow_config.supervisor`.
//
// Loop-edge → review_loop heuristic (per spec):
//   * For the "supervisor" canvas template ONLY, if `_hasLoopEdge` is
//     true, mode is "review_loop"; otherwise mode is "fan_out".
//   * Other templates ignore loop edges entirely (their mode is fixed).
// This keeps the inference intentionally conservative — we don't want
// a stray loop edge on a sequential chain to silently flip its mode.
// ---------------------------------------------------------------------
// ---------------------------------------------------------------------
// PUBLIC: collectUnsavedExpertPayloads(nodes)
//
// Walk the canvas, find agent-typed nodes (specialists / experts /
// workers) that DON'T yet have a backing agent_id, and emit them as
// expert-shaped payloads ready for `experts: [...]` on the /tree call.
//
// Used by BuilderView's atomic-save handler (§19.6) — when the user has
// dropped Sub-Agents and authored them via ExpertWizard, the wizard
// captures spec into the canvas node's agentConfig instead of writing
// directly to the DB. Save then collects them via this helper and the
// supervisor + experts get created in one /tree transaction.
//
// Each output entry uses the keys apply_tree's INSERT branch reads:
// name, system_prompt, channels, flow_config, domain_grammar,
// metadata_json. agent_id is omitted (apply_tree treats absent
// agent_id as INSERT).
// ---------------------------------------------------------------------
export function collectUnsavedExpertPayloads(nodes) {
  const expertNodes = _collectExpertNodes(nodes);
  return expertNodes
    .filter((n) => !n.agentConfig?.agentId)
    .map((n) => {
      const cfg = n.agentConfig || {};
      const payload = {
        name: cfg.agentName || n.label || "Expert",
      };
      if (cfg.systemPrompt) payload.system_prompt = cfg.systemPrompt;
      if (Array.isArray(cfg.channels)) payload.channels = cfg.channels;
      if (cfg.domainGrammar) payload.domain_grammar = cfg.domainGrammar;
      const flowConfig = {};
      if (Array.isArray(cfg.toolsets) && cfg.toolsets.length > 0) {
        flowConfig.api = { enabled: true, internal_toolsets: cfg.toolsets };
      }
      if (Object.keys(flowConfig).length > 0) {
        payload.flow_config = flowConfig;
      }
      const metadata = {};
      if (cfg.description) metadata.description = cfg.description;
      metadata.authored_via = cfg.authoredVia || "expert_wizard";
      if (Object.keys(metadata).length > 0) {
        payload.metadata_json = metadata;
      }
      return payload;
    });
}


// D9: the saved-expert agent_ids currently on the canvas. A saved expert node
// carries agentConfig.agentId; an unsaved (wizard-authored) one does not. Used
// with the loaded baseline to compute which experts the user deleted.
export function collectSavedExpertIds(nodes) {
  return new Set(
    _collectExpertNodes(nodes)
      .map((n) => n.agentConfig?.agentId)
      .filter(Boolean)
  );
}

// D9: ids present in the loaded baseline but no longer on the canvas → the user
// deleted them. apply_tree soft-deletes experts/workflows ONLY via explicit
// removed_*_ids (its declarative reconcile covers bindings, not experts), so
// the canvas MUST send these or a deleted expert lingers as a live orphan.
export function computeRemovedIds(loadedIds, currentIds) {
  const current = currentIds instanceof Set ? currentIds : new Set(currentIds || []);
  const loaded = loadedIds instanceof Set ? [...loadedIds] : (loadedIds || []);
  return loaded.filter((id) => id && !current.has(id));
}


export function inferSupervisorShape(nodes, connections) {
  const templateId = _detectTemplateId(nodes, connections);
  if (!templateId) return null;

  let mode = MODE_BY_TEMPLATE_ID[templateId];
  if (templateId === "supervisor" && _hasLoopEdge(connections)) {
    mode = "review_loop";
  }

  // Collect experts.  A canvas authored from the "supervisor" template
  // has the supervisor itself as an agent-typed node (filtered out by
  // _collectExpertNodes via executionMode==="supervisor").  Other
  // templates have NO supervisor-typed node — every agent node IS an
  // expert.
  const expertNodes = _collectExpertNodes(nodes);
  const experts = expertNodes.map((n) => ({
    name: n.agentConfig?.agentName || n.label || "Expert",
    agentId: n.agentConfig?.agentId || null,
  }));

  // max_iterations from supervisor node's config when present (the
  // supervisor template seeds {maxIterations: 5} on the supervisor
  // node).  Default falls through to the caller's choice (3 per spec).
  let max_iterations;
  if (templateId === "supervisor") {
    const supervisorNode = (nodes || []).find(
      (n) => n && n.type === "agent" && n.agentConfig?.executionMode === "supervisor",
    );
    const cfg = supervisorNode?.agentConfig?.supervisorConfig;
    if (cfg && typeof cfg.maxIterations === "number") {
      max_iterations = cfg.maxIterations;
    }
  }

  return { mode, experts, max_iterations, templateId };
}


// ---------------------------------------------------------------------
// PUBLIC: buildSupervisorPatch({ shape, agentName })
//
// Builds the partial-payload patch the caller merges into the agent
// payload before the API call.  The caller is responsible for:
//   * Reading the agent's existing `flow_config.supervisor` (so we
//     don't clobber `exemplars`, `routing_threshold`, etc. that the
//     auto-tune wizard or manual edits may have authored).
//   * Doing the actual merge (this helper returns the *fields to
//     merge*, not the merged object).
//
// Returns:
//   {
//     agent_kind: "supervisor",
//     supervisor: {
//       mode: <string>,
//       experts: [<string>, ...],   // names only, per use-case shape
//       max_iterations: <int>,      // defaults to 3 if shape didn't specify
//     }
//   }
//
// Caller does:
//   payload.agent_kind = patch.agent_kind;
//   payload.flow_config.supervisor = { ...existingSupervisor, ...patch.supervisor };
// ---------------------------------------------------------------------
// D3 (H3): the canonical PUT /tree save serializer. Assembles the tree-save
// body from already-computed inputs and STAMPS `expected_tree_version` so the
// Builder save participates in the backend's optimistic-concurrency check
// (apply_tree returns 412 stale_tree_version when it disagrees). Before this,
// BuilderView hand-rolled the body inline and never sent the version, so two
// tabs saving the same agent silently clobbered each other and the
// stale-canvas handler was dead code. Pure — unit-tested in isolation.
export function buildTreeSavePayload({
  payload,
  unsavedExperts,
  integrationsAndWorkflows,
  expectedTreeVersion,
  removedExpertIds = [],
  removedWorkflowIds = [],
}) {
  const iw = integrationsAndWorkflows || {};
  return {
    // Concurrency token — apply_tree reads this from the body (agents.py).
    expected_tree_version: Number(expectedTreeVersion) || 0,
    supervisor: {
      // ASC-v2 (B1): self-describe the kind so apply_tree owns agent_kind — a
      // single putTree can create/flip a supervisor + its experts atomically,
      // no legacy full-row PUT to seed the kind first.
      agent_kind: payload.agent_kind,
      name: payload.name,
      system_prompt: payload.system_prompt,
      channels: payload.channels,
      flow_config: payload.flow_config,
      metadata_json: payload.metadata_json,
      priority: payload.priority,
      routing_intents: payload.routing_intents,
      is_default: payload.is_default,
      temperature: payload.temperature,
      model: payload.model,
      provider: payload.provider,
      max_tokens: payload.max_tokens,
    },
    experts: unsavedExperts,
    removed_expert_ids: removedExpertIds,
    integrations: {
      mcp_server_bindings: iw.mcp_server_bindings,
      openapi_integration_bindings: iw.openapi_integration_bindings,
    },
    workflows: iw.workflows,
    removed_workflow_ids: removedWorkflowIds,
  };
}


export function buildSupervisorPatch({ shape, agentName: _agentName, routing }) {
  if (!shape || !shape.mode) {
    throw new Error("buildSupervisorPatch: shape with .mode is required");
  }
  // _agentName parameter is reserved for future use (e.g. defaulting
  // an expert prompt) but is not consumed in v1; kept on the API so
  // the caller's call site doesn't change when we wire it later.
  const expertNames = (shape.experts || [])
    .map((e) => (e && e.name) || null)
    .filter((n) => typeof n === "string" && n.length > 0);

  const supervisor = {
    mode: shape.mode,
    experts: expertNames,
    max_iterations:
      typeof shape.max_iterations === "number" && shape.max_iterations > 0
        ? shape.max_iterations
        : 3,
  };
  // C3: margin-routing config authored from the Builder supervisor node. Only
  // stamped when the caller passes a config object — otherwise the caller's
  // {...existingSupervisor, ...patch.supervisor} merge preserves whatever
  // routing the Viewer / auto-tune already persisted (no clobber, no default).
  if (routing && typeof routing === "object") {
    supervisor.routing = clampRoutingConfig(routing);
  }

  return { agent_kind: "supervisor", supervisor };
}


// ---------------------------------------------------------------------
// PUBLIC: pickTemplateForLoadedSupervisor(agent)
//
// Reverse direction: when BuilderView's load handler hits the "no
// canvas data" fallback for an agent_kind='supervisor' row, we hand
// the agent to this helper and it picks the canvas template that best
// represents the persisted mode.
//
// Returns one entry from ORCHESTRATION_TEMPLATES (always the
// "supervisor" template post-fix), or null if the agent isn't a
// supervisor / mode is unknown.
//
// 2026-05-24 fix (builder-supervisor-node-in-all-modes-fix-v1.md):
// All 4 modes now hydrate through the "supervisor" template so the
// supervisor node is ALWAYS visible on canvas. Mode-specific edge
// rewiring happens in hydrateSupervisorCanvasFromTree via
// buildSpecialistConnections(). Prior to this fix, sequential and
// conditional modes used the sequential-chain / conditional-routing
// templates respectively — those templates have no supervisor node,
// so the supervisor entity (the agent_kind='supervisor' row) was
// silently invisible. Bindings anchored on the supervisor's UUID
// also failed to find an anchor canvas node, falling back to
// agentNodes[0] (a random expert) for the integration's edge target.
// ---------------------------------------------------------------------
export function pickTemplateForLoadedSupervisor(agent) {
  if (!agent || agent.agent_kind !== "supervisor") return null;
  const mode = agent.flow_config?.supervisor?.mode;
  if (!mode) return null;

  // All four modes (fan_out, review_loop, sequential, conditional)
  // hydrate through the "supervisor" template — the only one carrying
  // a node with executionMode="supervisor". Mode-specific edge shapes
  // are applied downstream by buildSpecialistConnections().
  if (!["fan_out", "review_loop", "sequential", "conditional"].includes(mode)) {
    return null;
  }

  const tmpl = ORCHESTRATION_TEMPLATES.find((t) => t.id === "supervisor");
  return tmpl || null;
}


// ---------------------------------------------------------------------
// PUBLIC: hydrateIntegrationsAndWorkflows({nodes, connections, wiring, treeWorkflows})
//
// Phase C per agent-builder-v1.md §10 row 9. Load-side counterpart to
// inferIntegrationsAndWorkflows (C8). Given the canvas state already
// hydrated by pickTemplateForLoadedSupervisor / buildRegularCanvas,
// appends:
//   - mcp_integration / openapi_integration nodes for each binding
//     (from B4 /wiring's `bindings` array — extended by C9)
//   - workflow nodes for each workflow attached to the agent family
//     (from /tree's `workflows` array — already returned by B1)
// plus connections per §5.4 (provides_tools / triggered_by).
//
// Layout: integration nodes go BELOW the bound agent at +120 y; workflow
// nodes go ABOVE the entry agent at -120 y. Stacked vertically when
// multiple bindings share an agent.
// ---------------------------------------------------------------------
export function hydrateIntegrationsAndWorkflows({ nodes, connections, wiring, treeWorkflows }) {
  const N = [...(nodes || [])];
  const C = [...(connections || [])];
  const bindings = (wiring && wiring.bindings) || [];
  const workflows = treeWorkflows || [];

  // Map agent_id (or supervisor id) → existing canvas agent node.
  // Canvas node carries agentConfig.agentId when bound; supervisor's own
  // node has executionMode==="supervisor". Ids might be missing for
  // template-hydrated canvases — we fall back to the FIRST agent node
  // as the representative.
  const agentNodes = N.filter(n => n && (n.type === "agent" || n.type === "llm"));
  const byAgentId = new Map();
  for (const n of agentNodes) {
    const aid = n.agentConfig?.agentId || n.agentId;
    if (aid) byAgentId.set(aid, n);
  }
  const fallbackAgentNode = agentNodes[0] || null;

  // Track per-anchor stack offsets so multiple bindings to the same
  // agent don't pile on top of each other.
  //
  // Layout (user-reported BUG-024 follow-up 2026-05-23): integrations
  // sit BELOW their bound agent, NOT to the right. The earlier right-
  // of-anchor placement (+200 X) put integrations in the gap between
  // consecutive experts in a sequential chain — the chain edge
  // (expert-i → expert-i+1) then crosses through the FastAPI node and
  // the canvas looks like a tangle of crossings. Below-the-agent
  // separates the "this agent's wiring" branch (vertical) from the
  // "next agent in chain" flow (horizontal); chain edges run cleanly
  // between agents at y=260 while integrations branch downward.
  //
  // Stacking: first binding 100px below, subsequent +70 per slot.
  const slotBelow = new Map();
  const offsetAbove = new Map();
  const integrationYOffset = 100;  // distance BELOW anchor for slot 0
  const integrationYStep = 70;     // per-slot vertical stack
  const workflowYStep = 90;

  let counter = 1;
  const nextId = (kind) => `${kind}-hydrated-${counter++}`;

  // Integration tiles the canvas ALREADY carries, keyed by
  // `${kind}:${integration_id}` (same key the orphan-preservation pass in
  // BuilderView._appendBindingsForAgent uses). Two callers depend on this:
  //
  //  1. A saved canvas persists the tiles a previous load appended
  //     (metadata_json.canvas.nodes), so re-appending unconditionally
  //     stacked a second tile per binding on every open. A binding made
  //     from the Wiring tab has canvas_node_id = NULL, so the regenerated
  //     id collides with the saved node's id instead of replacing it —
  //     surfaced as "Builder shows multiple bindings of these OpenAPI
  //     integrations" (staging, 2026-08-02).
  //  2. BuilderView calls this twice per load — once for nodes, once for
  //     connections against the already-hydrated node list. The second
  //     pass MUST still emit edges for tiles the first pass created, so
  //     reuse the existing node as the edge endpoint rather than skipping
  //     the binding outright.
  const nodeByBindingKey = new Map();
  for (const n of N) {
    if (!n || !n.bound_id) continue;
    if (n.type !== "mcp_integration" && n.type !== "openapi_integration") continue;
    const kind = n.bound_kind || (n.type === "mcp_integration" ? "mcp" : "openapi");
    const key = `${kind}:${n.bound_id}`;
    if (!nodeByBindingKey.has(key)) nodeByBindingKey.set(key, n);
  }
  const hasEdge = (from, to, type) =>
    C.some(c => c && c.from === from && c.to === to && c.type === type);

  // Integration nodes — below the bound agent, stacked further down
  // within that vertical column when an agent has multiple bindings.
  for (const b of bindings) {
    const anchor = byAgentId.get(b.agent_id) || fallbackAgentNode;
    if (!anchor) continue;

    const isMcp = b.integration_kind === "mcp";
    let node = nodeByBindingKey.get(`${b.integration_kind}:${b.integration_id}`);
    if (!node) {
      const slot = slotBelow.get(anchor.id) || 0;
      slotBelow.set(anchor.id, slot + 1);
      node = {
        // A2 (agent-surface-consistency): key the id on integration_id, not a
        // call-local counter. nextId() reset to 1 each hydration, so a
        // NULL-canvas_node_id binding (Wiring-tab origin) always regenerated to
        // "<kind>-hydrated-1" and COLLIDED with an orphan saved node already
        // holding that id — two nodes, one id → the serializer emitted a
        // DUPLICATE binding (the uq_aib "save 500"). A bound_id-keyed id is
        // unique per integration and stable across hydrations.
        id: b.canvas_node_id || `${isMcp ? "mcp" : "openapi"}-${b.integration_id}`,
        type: isMcp ? "mcp_integration" : "openapi_integration",
        label: b.integration_name || b.integration_slug || (isMcp ? "MCP" : "OpenAPI"),
        icon: isMcp ? "\u{1F50C}" : "\u{1F517}",
        color: isMcp ? "#A855F7" : "#06B6D4",  // matches palette colors
        x: (anchor.x || 0),
        y: (anchor.y || 0) + integrationYOffset + slot * integrationYStep,
        bound_id: b.integration_id,
        bound_kind: b.integration_kind,
      };
      N.push(node);
      nodeByBindingKey.set(`${b.integration_kind}:${b.integration_id}`, node);
    }
    // §5.4 — connection FROM integration node TO bound agent (provides_tools).
    if (!hasEdge(node.id, anchor.id, "provides_tools")) {
      C.push({ from: node.id, to: anchor.id, type: "provides_tools" });
    }
    // Bidirectional pair — agent invokes integration; integration returns
    // tool result. Mirrors the LLM ↔ API/Tools dual-edge pattern in
    // regularCanvasFromFlow.js so two-way data flow reads visually
    // (request edge + return loop arc), no arrow markers needed.
    if (!hasEdge(anchor.id, node.id, "loop")) {
      C.push({ from: anchor.id, to: node.id, type: "loop", label: "Tool result" });
    }
  }

  // Workflow nodes
  for (const w of workflows) {
    const entryId = w.entry_agent_id;
    const anchor = (entryId && byAgentId.get(entryId)) || fallbackAgentNode;
    if (!anchor) continue;
    const existingOffset = offsetAbove.get(anchor.id) || 0;
    const offset = existingOffset + workflowYStep;
    offsetAbove.set(anchor.id, offset);

    const node = {
      id: nextId("wf"),
      type: "workflow",
      label: w.name || "Workflow",
      icon: "\u{1F501}",
      color: "#10B981",
      x: (anchor.x || 0) + 60,
      y: Math.max(20, (anchor.y || 0) - offset),
      bound_id: w.id,
      bound_kind: "workflow",
      status: w.status,
    };
    N.push(node);
    // §5.4 — connection FROM agent TO workflow (triggered_by).
    C.push({ from: anchor.id, to: node.id, type: "triggered_by" });
    // Bidirectional pair — workflow returns status / output back to the
    // triggering agent. Same pattern as provides_tools above.
    C.push({ from: node.id, to: anchor.id, type: "loop", label: "Status" });
  }

  return { nodes: N, connections: C };
}


// ---------------------------------------------------------------------
// PUBLIC: preserveOrphanCanvasNodes(builtNodes, savedCanvasNodes)
//
// When the supervisor-always-rehydrate path runs (BUG-024 v3), we
// rebuild positions from /tree + /wiring and discard saved positions.
// That's correct for experts and live integration bindings, but it
// also drops user-placed integration / workflow nodes whose binding
// was deleted from `agent_integration_bindings` after the canvas
// was last saved (real symptom on staging OC-01: saved canvas has a
// supabase mcp_integration with bound_id, but /wiring no longer
// returns that binding — so the node disappears).
//
// This helper finds saved canvas nodes of type mcp_integration /
// openapi_integration / workflow whose bound_id+bound_kind don't
// appear in the freshly-built node set, and appends them to the
// built nodes. y is clamped to ≥ 80 so the node lands inside the
// visible canvas-scroll area (saved coords could be negative if the
// canvas was previously panned/moved).
//
// Returns a new node array; doesn't mutate inputs.
// ---------------------------------------------------------------------
export function preserveOrphanCanvasNodes(builtNodes, savedCanvasNodes) {
  if (!Array.isArray(savedCanvasNodes) || savedCanvasNodes.length === 0) {
    return builtNodes;
  }
  if (!Array.isArray(builtNodes)) builtNodes = [];

  // Key by `${bound_kind}:${bound_id}` so the same integration bound
  // to two different agents both get preserved if neither is in
  // builtNodes. Workflows: key by `workflow:${bound_id}`.
  const builtKeys = new Set();
  for (const n of builtNodes) {
    if (!n) continue;
    const kind = n.bound_kind || (
      n.type === "mcp_integration" ? "mcp"
      : n.type === "openapi_integration" ? "openapi"
      : n.type === "workflow" ? "workflow"
      : null
    );
    if (kind && n.bound_id) {
      builtKeys.add(`${kind}:${n.bound_id}`);
    }
  }

  const orphans = [];
  for (const s of savedCanvasNodes) {
    if (!s) continue;
    const isBindableType = (
      s.type === "mcp_integration"
      || s.type === "openapi_integration"
      || s.type === "workflow"
    );
    if (!isBindableType) continue;
    if (!s.bound_id) continue;
    const kind = s.bound_kind || (
      s.type === "mcp_integration" ? "mcp"
      : s.type === "openapi_integration" ? "openapi"
      : "workflow"
    );
    const key = `${kind}:${s.bound_id}`;
    if (builtKeys.has(key)) continue;
    // Orphan — its live binding is gone from /wiring. Preserve it so the tile
    // doesn't silently vanish, but tag it `_orphan` so the serializer
    // (inferIntegrationsAndWorkflows) does NOT re-emit it (A4,
    // agent-surface-consistency): re-emitting would reactivate the
    // soft-deleted binding the user deliberately removed on the next save.
    // Clamp y/x so it lands inside the visible canvas-scroll area.
    orphans.push({
      ...s,
      _orphan: true,
      y: Math.max(80, s.y || 0),
      x: Math.max(80, s.x || 0),
    });
  }

  if (orphans.length === 0) return builtNodes;
  return [...builtNodes, ...orphans];
}


// ---------------------------------------------------------------------
// PUBLIC: inferIntegrationsAndWorkflows(nodes, connections, currentAgentId)
//
// Phase C per agent-builder-v1.md §10 row 8 + §5.4 connection semantics.
// Walks the canvas for the three new node types (C5/C6/C7) and emits the
// shape PUT /api/agents/:id/tree (C2) consumes:
//
//   {
//     "mcp_server_bindings": [
//       {mcp_server_id, bound_to_agent_id, canvas_node_id?}, ...
//     ],
//     "openapi_integration_bindings": [
//       {openapi_integration_id, bound_to_agent_id, canvas_node_id?}, ...
//     ],
//     "workflows": [
//       {name, definition_json, entry_agent_id?, entry_agent_node?,
//        status, channels?, workflow_id?}, ...
//     ]
//   }
//
// Connection semantics (§5.4):
//   * mcp_integration / openapi_integration → 'provides_tools' edge to
//     an agent node. We treat ANY connection OUT of an integration node
//     into an agent node as the binding target. Floating integration
//     nodes (no outgoing connection to an agent) are dropped — the
//     server-side endpoint also rejects them with 422 (§12 audit row).
//   * workflow ← 'triggered_by' edge from an agent node. We resolve the
//     entry_agent by following the incoming edge from any agent type.
//
// Backward compat: returns empty arrays when no new node types exist
// (regular trees save unchanged).
//
// currentAgentId — the agent being edited. Used as the binding target
// when the anchor agent node carries no explicit agentId of its own.
// This is NOT a nicety: apply_tree treats the emitted arrays as the
// COMPLETE desired set and soft-deletes every live binding missing from
// them. A regular agent's canvas (buildRegularCanvas) is a static
// pipeline — its `llm-1` node has no agentConfig.agentId — so before
// this fallback, every hydrated integration node resolved to null,
// got skipped, and the save silently unbound every API integration on
// the agent. Symptom: edit the Tools node → save → Wiring tab flips to
// "Not bound" while the canvas still shows the tile (staging bug,
// 2026-08-02). Binding to the agent under edit is also just correct:
// that IS the agent the canvas belongs to.
// ---------------------------------------------------------------------
export function inferIntegrationsAndWorkflows(nodes, connections, currentAgentId) {
  const out = {
    mcp_server_bindings: [],
    openapi_integration_bindings: [],
    workflows: [],
  };
  const N = nodes || [];
  const C = connections || [];

  const isAgentLike = (n) =>
    n && (n.type === "agent" || n.type === "llm");

  const resolveAgentId = (node) => {
    if (!node) return null;
    return node.agentConfig?.agentId || node.agentId || currentAgentId || null;
  };

  // ── Integration nodes — outgoing edges decide the bound agent ──
  // A3 (agent-surface-consistency): dedup by (agent, kind, integration_id) so
  // two canvas nodes referencing the SAME integration bound to the SAME agent
  // (an id-collision, or an orphan tile + a live node) serialize to ONE binding,
  // never a duplicate the backend would have to collapse (uq_aib).
  const _seenBinding = new Set();
  for (const node of N) {
    if (node?.type !== "mcp_integration" && node?.type !== "openapi_integration") continue;
    // A4: an orphan tile represents a binding the user already removed — never
    // re-serialize it (that would reactivate the soft-deleted binding on save).
    if (node._orphan) continue;
    const out_edge = C.find(c => c.from === node.id);
    if (!out_edge) continue;  // floating — server will 422 if accidentally serialized
    const target = N.find(n => n.id === out_edge.to);
    if (!isAgentLike(target)) continue;
    const bound_to_agent_id = resolveAgentId(target);
    if (!bound_to_agent_id) continue;  // unresolved canvas-only agent — skip
    if (!node.bound_id) continue;       // require integration_id on the node

    const _bkey = `${node.type}|${bound_to_agent_id}|${node.bound_id}`;
    if (_seenBinding.has(_bkey)) continue;  // A3: already emitted this exact binding
    _seenBinding.add(_bkey);

    const entry = {
      bound_to_agent_id,
      canvas_node_id: node.id,
    };
    if (node.type === "mcp_integration") {
      out.mcp_server_bindings.push({ ...entry, mcp_server_id: node.bound_id });
    } else {
      out.openapi_integration_bindings.push({ ...entry, openapi_integration_id: node.bound_id });
    }
  }

  // ── Workflow nodes — incoming edge from an agent decides entry_agent ──
  for (const node of N) {
    if (node?.type !== "workflow") continue;
    if (node._orphan) continue;  // A4: don't re-serialize a removed workflow tile
    const in_edge = C.find(c => c.to === node.id);
    const source = in_edge ? N.find(n => n.id === in_edge.from) : null;
    const entry_agent_id = resolveAgentId(source);
    out.workflows.push({
      workflow_id: node.bound_id || null,         // null = INSERT, set = UPDATE
      name: node.label || (node.bound_label || "Workflow"),
      entry_agent_id: entry_agent_id || null,
      definition_json: node.definition_json || {},
      status: node.status || "draft",
      channels: node.channels || [],
    });
  }

  return out;
}


// ---------------------------------------------------------------------
// PUBLIC: hydrateSupervisorCanvasFromTree({ template, experts })
//
// Generates a canvas {nodes, connections} for a supervisor-pattern
// template, dynamically scaling the specialist count to match the
// actual experts loaded from /api/agents/<id>/tree. Replaces the
// previous "rename the 2 template specialist nodes from
// flow_config.supervisor.experts" path which had two bugs:
//   1. flow_config.supervisor.experts holds expert UUIDs, not names —
//      so the labels rendered as "da3c4fc0-…" instead of the actual
//      expert names.
//   2. The "supervisor" canvas template has only 2 specialist slots
//      (Specialist A + Specialist B) — families with 4+ experts had
//      members silently dropped from the canvas.
//
// Inputs:
//   template — entry from ORCHESTRATION_TEMPLATES (supervisor /
//              sequential-chain / conditional-routing / fan-out-fan-in)
//   experts  — array of {id, name} from /tree response
//
// Output:
//   { nodes, connections } — same shape as template, but with N
//   specialist nodes (one per expert) instead of the template's hard-
//   coded 2. Each specialist node carries the expert's `id` as its
//   `agentConfig.agentId` so the binding is already complete on load
//   and the user can immediately drill in or save without re-binding.
//
// Layout strategy (supervisor template):
//   - Trigger / supervisor / action stay where the template puts them.
//   - Specialists stack vertically to the right of the supervisor,
//     spaced 140px apart, centered around the supervisor's y.
//
// Layout for sequential-chain: place specialists left-to-right in
// order. For conditional-routing: same fan layout as supervisor (one
// classifier-style supervisor at the center, specialists around).
// ---------------------------------------------------------------------
// ---------------------------------------------------------------------
// PUBLIC: isCanvasPathological(nodes)
//
// Detects the BUG-024 layout pathology: a saved Supervisor canvas
// where ≥5 agent/integration nodes are piled in a single ~40px-wide
// vertical column (no horizontal spread). When BuilderView spots this
// on load, it ignores the saved positions and re-hydrates from the
// agent tree so the new grid layout takes effect — self-healing for
// canvases saved before the layout fix landed.
//
// Heuristic — not a version check — so it doesn't require touching the
// save path. False-positive risk is low because Supervisor canvases
// are auto-generated from the tree shape; users rarely hand-arrange
// 5+ nodes into a tight column.
// ---------------------------------------------------------------------
export function isCanvasPathological(nodes) {
  if (!Array.isArray(nodes) || nodes.length < 5) return false;
  const candidates = nodes.filter((n) => n && (
    n.type === "agent" || n.type === "mcp_integration" || n.type === "openapi_integration"
  ));
  if (candidates.length < 5) return false;
  const buckets = new Map();
  for (const n of candidates) {
    const bucket = Math.round((n.x || 0) / 40);
    buckets.set(bucket, (buckets.get(bucket) || 0) + 1);
  }
  for (const count of buckets.values()) {
    if (count >= 5) return true;
  }
  return false;
}


export function hydrateSupervisorCanvasFromTree({ template, experts, mode, agent }) {
  if (!template) return null;
  // Back-compat: if caller didn't pass mode (legacy code path), infer it
  // from the template id. New callers post-fix should always pass mode
  // explicitly so the function can apply mode-specific edge wiring even
  // when the template is always the "supervisor" template.
  const effectiveMode = mode || MODE_BY_TEMPLATE_ID[template.id] || "fan_out";
  // Re-order experts to match flow_config.supervisor.experts (the
  // canonical order). /tree returns experts in DB-row order which is
  // arbitrary; for sequential mode the chain order MUST match the
  // configured order or expert.input depends on the wrong predecessor.
  // 2026-05-24 fix (builder-supervisor-node-in-all-modes-fix-v1.md
  // Bug-2B). When agent isn't passed (legacy callers), fall back to
  // /tree's order — same behavior as pre-fix.
  const rawExpertList = Array.isArray(experts) ? experts : [];
  const intendedOrder = agent?.flow_config?.supervisor?.experts;
  const expertList = (Array.isArray(intendedOrder) && intendedOrder.length > 0)
    ? (() => {
        // D7: flow_config.supervisor.experts has TWO stored conventions —
        // UUIDs (Builder tree-save, normalized by _tree_apply) and NAMES
        // (older use-case installs). Resolve either, mirroring the backend's
        // tolerant `_resolve_expert_by_id_or_name`. Key the lookup by BOTH so
        // an id-list and a name-list both order correctly.
        const byKey = new Map();
        for (const e of rawExpertList) {
          if (e?.id) byKey.set(e.id, e);
          if (e?.name) byKey.set(e.name, e);
        }
        // Dedup by resolved expert id — a name-list previously fell through
        // the id-keyed dedup and re-appended every expert, doubling them.
        const seen = new Set();
        const ordered = [];
        for (const key of intendedOrder) {
          const e = byKey.get(key);
          if (e && e.id && !seen.has(e.id)) { seen.add(e.id); ordered.push(e); }
        }
        // Append any /tree-returned experts not named in intendedOrder
        // (drift safety — a sibling with the use_case_id but absent from the
        // configured list must not be silently dropped).
        for (const e of rawExpertList) {
          if (e?.id && !seen.has(e.id)) { seen.add(e.id); ordered.push(e); }
        }
        return ordered;
      })()
    : rawExpertList;

  // Find the seed specialist node in the template (any agent node
  // whose executionMode is NOT "supervisor"). We clone it N times.
  const seedSpecialist = template.nodes.find(
    (n) => n.type === "agent"
        && n.agentConfig
        && n.agentConfig.executionMode !== "supervisor",
  );
  // Find the supervisor node (executionMode === "supervisor"), if any.
  // Sequential-chain / conditional-routing templates may not have an
  // explicit supervisor node — in that case the first agent is the
  // entry point and we just chain specialists after it.
  const supervisorNode = template.nodes.find(
    (n) => n.type === "agent" && n.agentConfig?.executionMode === "supervisor",
  );

  // Non-specialist nodes carry over (trigger, action, supervisor itself).
  // 2026-05-24 fix (Bug 2A): the supervisor node must carry the actual
  // supervisor agent's UUID in agentConfig.agentId so that integration
  // bindings whose agent_id is the supervisor's UUID (the OC-01 pattern)
  // anchor correctly in hydrateIntegrationsAndWorkflows. Pre-fix the
  // supervisor was carried verbatim from the template with agentId=null;
  // byAgentId.get(supervisor_uuid) missed and the anchor fell back to
  // agentNodes[0], producing dangling edges when the fallback ID didn't
  // exist in the final hydrated canvas (timing race against /tree).
  const carriedNodes = template.nodes
    .filter((n) => !(n.type === "agent" && n.agentConfig
                     && n.agentConfig.executionMode !== "supervisor"))
    .map((n) => {
      const out = { ...n, agentConfig: n.agentConfig ? { ...n.agentConfig } : undefined };
      // Populate the supervisor node's identity from the agent row.
      if (n.type === "agent" && n.agentConfig?.executionMode === "supervisor" && agent?.id) {
        out.label = agent.name || out.label;
        out.agentConfig = {
          ...out.agentConfig,
          agentId: agent.id,
          agentName: agent.name || null,
          agentStatus: agent.status || null,
        };
      }
      return out;
    });

  // Build N specialist nodes, one per expert. If the family has zero
  // experts, return the template carried-nodes only (no specialists).
  //
  // Layout policy:
  //   - Sequential-chain → horizontal chain (A → B → C left-to-right).
  //   - Everything else → grid; more columns as N grows so a tenant's
  //     7-expert OC-01 doesn't degenerate into a 1-column 1000px-tall
  //     strip that's unreadable at any zoom level. Integrations stack
  //     below each expert (placed by hydrateIntegrationsAndWorkflows);
  //     rowHeight is sized to give them breathing room.
  const specialistNodes = [];
  const N = expertList.length;
  // Mode-driven: post-2026-05-24 fix, pickTemplateForLoadedSupervisor
  // always picks the "supervisor" template, so we can no longer key
  // layout off template.id. Sequential mode wants a horizontal chain;
  // every other mode wants the grid layout under the supervisor.
  const isSequential = effectiveMode === "sequential";
  // Columns: 1 (≤4), 2 (≤8), 3 (≤15), 4 (≥16).
  const numCols = isSequential
    ? Math.max(1, N)
    : (N <= 4 ? 1 : N <= 8 ? 2 : N <= 15 ? 3 : 4);
  // colWidth = 380 leaves room for the right-of-anchor integration node
  // (placed at anchor.x + 200 by hydrateIntegrationsAndWorkflows, ~170px
  // wide) without bleeding into the next grid column. rowHeight = 200
  // gives an integration stack (anchor.y + 0, +70, +140) ~60px clearance
  // to the next row's expert.
  const colWidth = 380;
  const rowHeight = 200;
  const baseY = supervisorNode?.y ?? seedSpecialist?.y ?? 260;
  const baseX = (supervisorNode?.x ?? seedSpecialist?.x ?? 360) + 320;
  let gridRightEdge = baseX;

  if (seedSpecialist && N > 0) {
    const numRows = isSequential ? 1 : Math.ceil(N / numCols);
    const stackHeight = (numRows - 1) * rowHeight;
    // Clamp to keep the top row inside the canvas. The canvas-scroll
    // container's overflow:auto clips anything with y < 0 — nodes
    // would render but stay invisibly above the scrollable region.
    // Floor of 80 leaves room for the pipeline-label overlay (line
    // ~1565 in BuilderView, top:18 height ~36).
    const startY = Math.max(80, baseY - stackHeight / 2);
    expertList.forEach((expert, idx) => {
      const col = isSequential ? idx : (idx % numCols);
      const row = isSequential ? 0 : Math.floor(idx / numCols);
      specialistNodes.push({
        ...seedSpecialist,
        id: `agent-expert-${idx + 1}`,
        label: expert.name || `Expert ${idx + 1}`,
        x: baseX + col * colWidth,
        y: startY + row * rowHeight,
        agentConfig: {
          ...seedSpecialist.agentConfig,
          agentId: expert.id || null,
          agentName: expert.name || null,
          agentStatus: expert.status || null,
        },
      });
    });
    gridRightEdge = baseX + (numCols - 1) * colWidth;
  }

  // Push downstream carried-nodes (aggregator / action) past the grid
  // AND past the integration column that hydrateIntegrationsAndWorkflows
  // will subsequently place at gridRightEdge + 200 (~170px wide). With
  // +260 alone an N=4 supervisor (1 col) would render the action on top
  // of an OpenAPI integration node. The +440 accounts for the 200px
  // integration offset + ~170px integration node width + ~70px gutter.
  if (seedSpecialist) {
    const seedX = seedSpecialist.x;
    const downstream = carriedNodes
      .filter((n) => n.x >= seedX)
      .sort((a, b) => a.x - b.x);
    let cursor = gridRightEdge + 440;
    for (const n of downstream) {
      n.x = cursor;
      n.y = baseY;
      cursor += 240;
    }
  }

  // Build connections + (for conditional mode) any synthesized
  // intermediate nodes (the condition-router). Mode-dispatched per the
  // 2026-05-24 fix (builder-supervisor-node-in-all-modes-fix-v1.md).
  const { connections, extraNodes } = buildSpecialistConnections({
    mode: effectiveMode,
    template,
    specialistNodes,
    supervisorNode,
  });

  const nodes = [...carriedNodes, ...extraNodes, ...specialistNodes];

  return { nodes, connections };
}


// ---------------------------------------------------------------------
// PUBLIC: populateSupervisorIdentity(templateNodes, agent)
//
// Returns a copy of `templateNodes` with the supervisor-typed node's
// agentConfig.{agentId,agentName,agentStatus} + label populated from
// the `agent` row. Used by the IMMEDIATE stock-template render path
// in BuilderView (before /tree resolves) so that the supervisor node
// carries its real identity even during the brief stock render. This
// matters because _appendBindingsForAgent races against /tree and
// runs anchor lookups against whatever node array is in state at
// that moment — if the stock template still has agentId=null,
// bindings anchored on the supervisor's UUID dangle on a fallback.
//
// 2026-05-24 fix (builder-supervisor-node-in-all-modes-fix-v1.md
// Bug 2A). See sibling logic in hydrateSupervisorCanvasFromTree
// which does the equivalent for the post-/tree hydrated render.
// ---------------------------------------------------------------------
export function populateSupervisorIdentity(templateNodes, agent) {
  if (!Array.isArray(templateNodes) || !agent?.id) {
    return (templateNodes || []).map((n) => ({
      ...n, agentConfig: n.agentConfig ? { ...n.agentConfig } : undefined,
    }));
  }
  return templateNodes.map((n) => {
    const out = { ...n, agentConfig: n.agentConfig ? { ...n.agentConfig } : undefined };
    if (n.type === "agent" && n.agentConfig?.executionMode === "supervisor") {
      out.label = agent.name || out.label;
      out.agentConfig = {
        ...out.agentConfig,
        agentId: agent.id,
        agentName: agent.name || null,
        agentStatus: agent.status || null,
      };
    }
    return out;
  });
}


// ---------------------------------------------------------------------
// PUBLIC: buildSpecialistConnections({ mode, template, specialistNodes, supervisorNode })
//
// Mode-dispatched connection builder for the supervisor canvas.
// Returns { connections, extraNodes } — extraNodes are any synthesized
// intermediate nodes (today only the condition-router for conditional
// mode). The caller is responsible for merging extraNodes into the
// final nodes array between carriedNodes and specialistNodes.
//
// Modes:
//   - fan_out / review_loop  — supervisor fans out to each expert and
//     loops back. Reuses the existing template-driven rewire so the
//     fan-out behavior is byte-identical to pre-fix.
//   - sequential             — supervisor → expert-1 → expert-2 → … →
//     expert-N → action (shape (a) per the design doc — supervisor at
//     entry only, experts chain directly, no per-step re-synthesis).
//   - conditional            — supervisor → router → one-of {experts}
//     → action. Synthesizes a single condition-router node between
//     supervisor and experts.
//
// All four modes assume the supervisor template is in use (post-fix
// pickTemplateForLoadedSupervisor always returns the supervisor
// template). The supervisor template's hardcoded specialist
// connections are discarded; only the trigger→supervisor edge is
// reused from the template.
//
// Exported for unit testing — see frontend/src/features/builder/
// __tests__/buildSpecialistConnections.test.js (Phase 2 Step 5).
// ---------------------------------------------------------------------
export function buildSpecialistConnections({ mode, template, specialistNodes, supervisorNode }) {
  const supervisorId = supervisorNode?.id || "agent-1";
  const triggerNode = (template.nodes || []).find((n) => n && n.type === "trigger");
  const actionNode = (template.nodes || []).find((n) => n && n.type === "action");
  const triggerId = triggerNode?.id || "trigger-1";
  const actionId = actionNode?.id || "action-1";

  // Empty experts list: carry over only the non-specialist edges from
  // the template (trigger → supervisor, supervisor → action). The
  // canvas renders as a trivial trigger → supervisor → action chain.
  if (specialistNodes.length === 0) {
    const seedSpecialistIds = new Set(
      (template.nodes || [])
        .filter((n) => n.type === "agent" && n.agentConfig
                    && n.agentConfig.executionMode !== "supervisor")
        .map((n) => n.id),
    );
    const connections = (template.connections || [])
      .filter((c) => !seedSpecialistIds.has(c.from) && !seedSpecialistIds.has(c.to))
      .map((c) => ({ ...c }));
    return { connections, extraNodes: [] };
  }

  if (mode === "sequential") {
    // Cross-mode consistent shape: the supervisor IS the orchestrator
    // that returns the response (→ action). Internal execution shape
    // (chain) is independent of the return path. Matches fan_out and
    // review_loop which already wire supervisor → action via the
    // template's carried connections.
    //   trigger → supervisor → action     (spine)
    //   supervisor → expert-1, ..., expert-N    (ownership)
    //   expert-1 → expert-2 → ... → expert-N    (chain: execution order)
    const connections = [
      { from: triggerId, to: supervisorId },
      { from: supervisorId, to: actionId },
    ];
    for (const s of specialistNodes) {
      connections.push({ from: supervisorId, to: s.id });
      // Return loop — each specialist returns its answer to the
      // supervisor (the orchestrator that owns the final synthesis).
      // Dual-arc pattern matches LLM ↔ API/Tools so two-way data flow
      // reads without arrow markers; multiple parallel returns stagger
      // their dropY in CanvasConnections.jsx.
      connections.push({
        from: s.id,
        to: supervisorId,
        type: "loop",
        label: "Answer",
      });
    }
    for (let i = 0; i < specialistNodes.length - 1; i++) {
      connections.push({
        from: specialistNodes[i].id,
        to: specialistNodes[i + 1].id,
        type: "sequential",
      });
    }
    return { connections, extraNodes: [] };
  }

  if (mode === "conditional") {
    // Cross-mode consistent shape: trigger → supervisor → action spine
    // + supervisor delegates via Router to one of N experts. The
    // dispatched expert's response flows back to supervisor implicitly
    // (no per-expert → action edge needed; the supervisor returns).
    //   trigger → supervisor → action     (spine)
    //   supervisor → router → expert-1, ..., expert-N    (dispatch)
    const routerId = "__supervisor_synth_condition_router";
    const supervisorX = supervisorNode?.x || 360;
    const supervisorY = supervisorNode?.y || 260;
    const routerNode = {
      id: routerId,
      type: "condition",
      label: "Router",
      icon: "◇",
      color: "#F59E0B",
      x: supervisorX + 160,
      y: supervisorY,
    };
    const connections = [
      { from: triggerId, to: supervisorId },
      { from: supervisorId, to: actionId },
      { from: supervisorId, to: routerId },
    ];
    for (const s of specialistNodes) {
      connections.push({ from: routerId, to: s.id, type: "conditional" });
      // Return loop — dispatched expert returns its answer to the
      // supervisor (skips the router; router is dispatch-only).
      connections.push({
        from: s.id,
        to: supervisorId,
        type: "loop",
        label: "Answer",
      });
    }
    return { connections, extraNodes: [routerNode] };
  }

  // fan_out / review_loop / unrecognized → existing template-driven
  // fan-out rewire. Preserves the pre-fix behavior byte-for-byte for
  // these modes (RR1 mitigation in design doc).
  const seedSpecialistIds = new Set(
    (template.nodes || [])
      .filter((n) => n.type === "agent" && n.agentConfig
                  && n.agentConfig.executionMode !== "supervisor")
      .map((n) => n.id),
  );
  const connections = [];
  for (const c of (template.connections || [])) {
    const fromIsSpecialist = seedSpecialistIds.has(c.from);
    const toIsSpecialist = seedSpecialistIds.has(c.to);
    if (!fromIsSpecialist && !toIsSpecialist) {
      // Trigger → supervisor, supervisor → action, etc. Keep verbatim.
      connections.push({ ...c });
      continue;
    }
    if (fromIsSpecialist && toIsSpecialist) {
      // Both ends are seed specialists — template intent is a CHAIN.
      // Pair across the new specialists: expert-1 → expert-2, etc.
      for (let i = 0; i < specialistNodes.length - 1; i++) {
        connections.push({
          ...c,
          from: specialistNodes[i].id,
          to:   specialistNodes[i + 1].id,
        });
      }
      continue;
    }
    // One end is a specialist; fan out across all specialists.
    for (const s of specialistNodes) {
      connections.push({
        ...c,
        from: fromIsSpecialist ? s.id : c.from,
        to:   toIsSpecialist   ? s.id : c.to,
      });
    }
  }
  return { connections, extraNodes: [] };
}
